import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onFinish", "children", "videoType", "videoId", "autoPlay", "playSpeed", "language", "onProgress", "onPlay", "videoDuration", "currentTime", "completedVideoView"],
  _excluded2 = ["contentId", "lessonId", "trackId", "language"],
  _excluded3 = ["shortId", "shortName", "language", "playlistId"],
  _excluded4 = ["shortId", "language"];
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { TrackingContentTypes } from '../../../constants/Tracking';
import { updateContentProgress } from '../../../features/userProgress';
import ContentViewport from '../../../components/ContentViewport';
import { useVideo } from '../../../features/video/videoQueries';
import { Metrics } from '../../../Metrics';
import getLang from 'LearningCenterUI/utils/getLang';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
// @ts-expect-error Could not find a declaration file for module// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../../hooks/searchParamHooks';
import omit from 'hs-lodash/omit';
import { useShortContentParams, useShortVideo, useStartShort } from '../../../features/shortContent';
import { EmbeddedVideo, useAutoClearVideoVTS, useVideoParams } from '..';
import { convertToMinutes } from '../../../utils/utils';
import UsageTracker from '../../../utils/UsageTracker';
import styled, { theme } from 'academy-common-lib/utils/Styling';
import UIFlex from 'UIComponents/layout/UIFlex';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HS_VIDEO_CURRENT_TIME_TO_UPDATE_PROGRESS = 3;
const CompletedVideoView = styled(UIFlex).withConfig({
  displayName: "VideoPlayer__CompletedVideoView",
  componentId: "sc-1qr5wb8-0"
})(["position:absolute;background-color:", ";top:0;width:100%;height:", ";color:", ";"], theme.colors.MONOLITH, ({
  videoType
}) => videoType === 'HUBSPOT_VIDEO' ? 'calc(100% - 34px)' : '100%', theme.colors.OLAF);
const VideoPlayerContext = /*#__PURE__*/createContext({
  isShowingCompletedScreen: false,
  showCompletedScreen: () => {},
  hideCompletedScreen: () => {}
});
export const useVideoPlayerContext = () => useContext(VideoPlayerContext);
export const VideoPlayerProvider = ({
  children
}) => {
  const [isShowingCompletedScreen, setShowCompletedVideoScreen] = useState(false);
  const showCompletedScreen = () => setShowCompletedVideoScreen(true);
  const hideCompletedScreen = () => setShowCompletedVideoScreen(false);
  const contextState = {
    isShowingCompletedScreen,
    showCompletedScreen,
    hideCompletedScreen
  };
  return /*#__PURE__*/_jsx(VideoPlayerContext.Provider, {
    value: contextState,
    children: children
  });
};
const getStartTime = ({
  startTimeFromUrl,
  startTimeFromProps,
  videoDuration
}) => {
  const desiredTime = startTimeFromProps || startTimeFromUrl;
  if (desiredTime && videoDuration && desiredTime < videoDuration) {
    return {
      hasStartTime: true,
      startTime: desiredTime
    };
  }
  return {
    hasStartTime: false,
    startTime: 0
  };
};
const VideoPlayerBase = _ref => {
  let {
      onFinish,
      children,
      videoType,
      videoId,
      autoPlay: autoPlayProp,
      playSpeed,
      language,
      onProgress,
      onPlay,
      videoDuration,
      currentTime: startTimeFromProps,
      completedVideoView
    } = _ref,
    itemProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  useAutoClearVideoVTS();
  const secondsElapsed = useRef(0);
  const [hasClickedPlayForVideo, setHasClickedPlayForVideo] = useState(false);
  const [hasMarkedProgress, setHasMarkedProgress] = useState(false);
  const {
    startTime: startTimeFromUrl
  } = useVideoParams();
  const [currentTime, setCurrentTime] = useState();
  const {
    isShowingCompletedScreen,
    showCompletedScreen,
    hideCompletedScreen
  } = useVideoPlayerContext();
  const {
    hasStartTime,
    startTime
  } = getStartTime({
    startTimeFromUrl,
    startTimeFromProps,
    videoDuration: videoDuration
  });
  const isVidyardVideo = videoType === 'VIDYARD';
  const isHubspotVideo = videoType === 'HUBSPOT_VIDEO';
  const autoplay = autoPlayProp || hasStartTime && isHubspotVideo;
  const buildWhatExtraJson = useCallback((action, props) => {
    return Object.assign({
      action,
      contentLanguage: language,
      contentType: TrackingContentTypes.VIDEO,
      playerType: videoType
    }, props, omit(itemProps, 'PlayerComponent', 'onPlay'));
  }, [language, videoType, itemProps]);
  const handleVideoPlay = useCallback(() => {
    if (!hasClickedPlayForVideo) {
      setHasClickedPlayForVideo(true);
      if (onPlay) {
        onPlay();
      }
      UsageTracker.track('contentInteraction', buildWhatExtraJson('video-play'));
    }
    if (isHubspotVideo) {
      Metrics.counter('hs-video-played').increment();
    }
    if (!currentTime && hasStartTime) {
      setCurrentTime(startTime);
    }
  }, [buildWhatExtraJson, currentTime, hasClickedPlayForVideo, hasStartTime, isHubspotVideo, onPlay, startTime]);
  const handleUpdateProgress = useCallback(props => {
    UsageTracker.track('activation', buildWhatExtraJson('video-activation'));
    UsageTracker.track('contentProgress', buildWhatExtraJson('video-progression', props));
    if (onProgress) onProgress();
    hideCompletedScreen();
  }, [buildWhatExtraJson, hideCompletedScreen, onProgress]);

  //HS video uses progress event (https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/progress_event) to return current time of a video.
  //The progress event is triggered every time when a video's buffer level is updated.
  const handleProgressHsVideo = useCallback(e => {
    if (e && e.detail && e.detail.currentTime >= HS_VIDEO_CURRENT_TIME_TO_UPDATE_PROGRESS && !hasMarkedProgress) {
      const viewDuration = convertToMinutes(e.detail.currentTime - secondsElapsed.current);
      if (viewDuration) {
        setHasMarkedProgress(true);
        handleUpdateProgress({
          viewDuration
        });
      }
      secondsElapsed.current = e.detail.currentTime;
    }
  }, [handleUpdateProgress, hasMarkedProgress]);
  const handleVideoFinish = useCallback(() => {
    UsageTracker.track('contentInteraction', buildWhatExtraJson('video-finished'));
    if (isHubspotVideo) {
      Metrics.counter('hs-video-finished').increment();
    }
    const viewDuration = videoDuration && convertToMinutes(videoDuration - secondsElapsed.current);
    if (onFinish) {
      onFinish();
      if (!hasMarkedProgress && viewDuration) handleUpdateProgress({
        viewDuration
      });
    }
    showCompletedScreen();
  }, [buildWhatExtraJson, handleUpdateProgress, hasMarkedProgress, isHubspotVideo, onFinish, showCompletedScreen, videoDuration]);
  const handleOnProgress = isVidyardVideo ? handleUpdateProgress : handleProgressHsVideo;
  useEffect(() => {
    if (videoId && isHubspotVideo) setHasMarkedProgress(false);
  }, [isHubspotVideo, videoId]);
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "embedded-video",
    children: [/*#__PURE__*/_jsxs(ContentViewport, {
      wrapperStyle: {
        position: 'relative'
      },
      children: [/*#__PURE__*/_jsx(EmbeddedVideo, Object.assign({
        autoPlay: autoplay,
        currentTime: currentTime,
        language: language,
        onClick: hideCompletedScreen,
        onFinish: handleVideoFinish,
        onPlay: handleVideoPlay,
        onProgress: handleOnProgress,
        playSpeed: playSpeed
      }, itemProps)), completedVideoView && isShowingCompletedScreen && /*#__PURE__*/_jsx(CompletedVideoView, {
        videoType: videoType,
        children: completedVideoView
      })]
    }), children]
  });
};
const VideoPlayerTrackLesson = _ref2 => {
  let {
      contentId,
      lessonId,
      trackId,
      language: languageProp
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const [[searchParamLanguage]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const language = languageProp || (searchParamLanguage || getLang()).toLowerCase();
  const handleOnProgress = useCallback(() => {
    if (language) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      updateContentProgress({
        contentId: Number(contentId),
        lessonId: Number(lessonId),
        trackId: Number(trackId),
        language
      });
    }
  }, [contentId, language, lessonId, trackId]);
  const videoProps = useVideo({
    contentId,
    lessonId,
    trackId,
    language
  });
  return /*#__PURE__*/_jsx(VideoPlayerBase, Object.assign({
    contentId: contentId,
    language: language,
    lessonId: lessonId,
    onProgress: handleOnProgress,
    trackId: trackId
  }, props, videoProps));
};
const VideoPlayerShort = _ref3 => {
  let {
      shortId,
      shortName,
      language: languageProp,
      playlistId
    } = _ref3,
    props = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  const {
    language: languageParam
  } = useShortContentParams();
  const language = languageProp || languageParam;
  const videoProps = useShortVideo({
    shortId: shortId,
    language: language,
    skip: !shortId || !language
  });
  const [startShort] = useStartShort(shortId, language, playlistId);
  const handleOnPlay = useCallback(() => {
    startShort().catch(Raven.captureException);
  }, [startShort]);
  if (!videoProps || !shortId || !language) return null;
  return /*#__PURE__*/_jsx(VideoPlayerBase, Object.assign({
    language: language,
    onPlay: handleOnPlay,
    shortId: shortId,
    shortName: shortName
  }, props, videoProps));
};
export const VideoPlayer = _ref4 => {
  let {
      shortId,
      language
    } = _ref4,
    props = _objectWithoutPropertiesLoose(_ref4, _excluded4);
  if (shortId && language) return /*#__PURE__*/_jsx(VideoPlayerShort, Object.assign({
    language: language,
    shortId: shortId
  }, props));else return /*#__PURE__*/_jsx(VideoPlayerTrackLesson, Object.assign({
    language: language
  }, props));
};
export default VideoPlayer;